// Frontend i18n — translates labels/commentary only.
// English examples, suggested answers, vocabulary suggestions and the interview
// question text itself always stay in English.
//
// Bundles are keyed by BCP-47 code (en, pt-BR, es-419, fr-CA) with two
// backward-compat aliases: "pt-br" and "both" (legacy session.language codes)
// map to the pt-BR bundle. makeT() normalises whichever the caller passes.

const STRINGS = {
  en: {
    // Common
    "brand.tagline": "AI Interview Simulator",
    "common.copy": "Copy",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Download Assessment Report",
    "rp.downloadsCardH2": "Get My Study Guide",
    "rp.downloadsCardSub1": "Get your full question-by-question performance report.",
    "rp.downloadsCardSub2": "Get a personalized 7-day study guide based on your interview performance.",
    "rp.downloadsCardBtn1": "Download Report",
    "rp.downloadsCardBtn2": "Download Study Guide",
    "rp.downloadsCardBuilding": "Preparing…",
    "rp.pdfFormat": "PDF format",
    // Interview page (still hardcoded on some screens — full rollout in a later phase)
    "iv.progress": (n, t, a) => `Question ${n} of ${t}   ${a} answered`,
    "iv.finish": "See my report",
    "iv.finish.busy": "Building report…",
    "iv.playQ": "Play question",
    "iv.stopQ": "Stop",
    "iv.loading": "Loading…",
    "iv.femaleVoice": "Female voice",
    "iv.maleVoice": "Male voice",
    "iv.restartInterview": "Restart interview",
    "iv.restartConfirm": "Restart the interview? Your current progress will be lost, but your uploaded résumé, job description and other selections will be kept.",
    "iv.recordAria": "Start recording",
    "iv.stopAria": "Stop recording",
    "iv.micAccessDenied": "Please allow microphone access in your browser.",
    "iv.interviewCompleteTitle": "Interview complete!",
    "iv.interviewCompleteBody": "Congratulations! You've successfully completed your interview. You can now view your full performance report and personalized study plan.",
    "iv.progressCurrent": (n, total) => `Question ${n} of ${total}`,
    "iv.progressAnswered": (n) => `${n} answered`,
    "iv.hiddenH": "Listening comprehension mode",
    "iv.hiddenP": "The question text is hidden. Listen carefully to the interviewer, then answer with your voice. You can replay the question above at any time.",
    "iv.reveal": "Reveal the question text →",
    "iv.tapMic": "Tap the mic to speak",
    "iv.recording": "Recording your answer",
    "iv.recorded": "Recorded — review before sending",
    "iv.rerecord": "Re-record",
    "iv.submit": "Submit answer",
    "iv.submitting": "Transcribing & scoring…",
    "iv.transcriptLabel": "Transcript",
    "iv.previous": "← Previous",
    "iv.skipNext": "Skip to next",
    "iv.lastHint": "Last question — finish above when ready.",
    "iv.yourQuestions": "Your questions",
    "iv.hiddenSidebar": "Hidden — listen when you get here",
    "iv.answered": "Answer recorded",
    "iv.answeredSub": "You can review your feedback in the final report.",
    "iv.clarity": "Clarity",
    "iv.grammar": "Grammar",
    "iv.vocabulary": "Vocabulary",
    "iv.tone": "Tone",
    "iv.yourTranscript": "Your transcript",
    "iv.questionEyebrow": "Interview question",
    "iv.standardQuestion": "Standard question",
    "iv.roleQuestion": "Role-specific question",
    "iv.resumeQuestion": "Resume-based question",
    "iv.questionN": (n) => `Question ${n}`,
    "iv.loadFail": "Could not load your interview.",
    "iv.answerSaved": "Answer saved.",
    "iv.submitFail": "Failed to submit. Please try again.",
    "iv.reportFail": "Could not build the report yet.",
    "iv.audioPlayFail": "Could not play the question audio.",
    "iv.audioUnavailable": "The question audio is unavailable right now. Please read the question below and continue.",
    "iv.stop": "Stop",
    "iv.warmupEye": "Warm-up Question",
    "iv.warmupTitle": "Let's start with a quick warm-up",
    "iv.warmupIntro": "Before we begin the interview, we'll do a brief warm-up question. Its only purpose is to help you test your microphone, feel more comfortable speaking in English, and understand how the interview works. Your response is not evaluated and does not affect your score or reports.",
    "iv.warmupBanner": "This warm-up is just for you to get ready. Nothing is sent for analysis.",
    "iv.warmupStart": "Start recording",
    "iv.warmupContinue": "Continue to the interview",
    "iv.warmupQuestionEye": "Warm-up question",
    // Report page
    "rp.download": "Download report",
    "rp.newInterview": "New interview",
    "rp.betaFeedback": "Beta feedback",
    "rp.betaFeedbackStatusError": "Beta feedback is temporarily unavailable.",
    "rp.repracticeH": "Practice These Questions Again",
    "rp.repracticeP": "Apply your feedback by repeating the same interview questions.",
    "rp.repracticeMeta": "1 re-practice available · Uses 1 credit",
    "rp.repracticeCta": "Practice these questions again",
    "rp.repracticeUsed": "You\u2019ve completed your re-practice attempt for this interview.",
    "rp.repracticeConfirmTitle": "Practice These Questions Again?",
    "rp.repracticeConfirmIntro": "You can re-practice this interview once using the exact same questions. This will use 1 credit.",
    "rp.repracticeConfirmBeforeH": "Before you start:",
    "rp.repracticeConfirmBullet1": "Your Performance Report and Study Guide will remain available to download for 30 days.",
    "rp.repracticeConfirmBullet2": "Do not log out. Your Re-Practice must be completed during your current login session.",
    "rp.repracticeConfirmYes": "Start Re-Practice",
    "rp.repracticeConfirmNo": "Cancel",
    "rp.repracticeStarting": "Starting re-practice\u2026",
    "rp.repracticeErrGeneric": "Could not start the re-practice. Please try again.",
    "rp.repracticeErrAlreadyUsed": "You\u2019ve already used your re-practice for this interview.",
    "rp.repracticeErrNoCredits": "You don\u2019t have any interview credits remaining.",
    "rp.overall": "Overall performance",
    "rp.composite": "Composite of clarity · grammar · vocabulary · professional tone",
    "rp.answeredOf": (a, t) => `You completed ${a} of ${t} questions. Scroll down for per-question feedback — clarity, grammar, vocabulary and professional tone.`,
    "rp.clarity": "Clarity",
    "rp.grammar": "Grammar",
    "rp.vocabulary": "Vocabulary",
    "rp.protone": "Professional tone",
    "rp.fluency": "Fluency",
    "rp.relevance": "Relevance to the question",
    "rp.supportingEvidence": "Supporting evidence",
    "rp.organization": "Organization & structure",
    "rp.professionalCompetency": "Professional competency",
    "rp.evidenceOfResults": "Evidence of results",
    "rp.professionalPresence": "Professional presence",
    "rp.interviewPerformance": "Interview performance",
    "rp.englishLanguage": "Professional English communication",
    "rp.englishInsufficient": "Answer too short to assess English.",
    "rp.englishAssessedOf": (a, t) => `assessed on ${a} of ${t} answers`,
    "rp.summary": "Summary",
    "rp.summaryH": "Your Interview Performance at a Glance",
    "rp.summaryP": "Below is your question-by-question feedback. Review what you did well, where you can improve, and the areas that will have the greatest impact on your next interview.",
    "rp.bestArea": "Best area",
    "rp.focusNext": "Focus next",
    "rp.perQuestion": "Per-question feedback",
    "rp.answeredCount": (a, t) => `${a} of ${t} answered`,
    "rp.notAnswered": "Not answered",
    "rp.overallShort": "Overall",
    "rp.yourTranscript": "Your transcript",
    "rp.suggestedAnswer": "Suggested polished answer",
    "rp.strengths": "Strengths",
    "rp.improve": "Improve",
    // Job match
    "jm.title": "Job match",
    "jm.subtitle": "How your resume + answers stack up against the required skills in the job description.",
    "jm.matchScore": "Match score",
    "jm.matched": "matched",
    "jm.partial": "partial",
    "jm.gap": "gap",
    "jm.gaps": "gaps",
    "jm.covered": (m, t) => `Required skills · ${m}/${t} covered`,
    "jm.statusMatch": "Match",
    "jm.statusPartial": "Partial",
    "jm.statusGap": "Gap",
    // Study plan
    "sp.eyebrow": "Next step",
    "sp.hGaps": (n) => "top " + (n === 1 ? "priority" : `${n} gaps`),
    "sp.hFallback": "top improvement areas",
    "sp.h": (target) => `Get a personalised 7-day study plan for your ${target}.`,
    "sp.pWithGaps": "We've spotted where your resume + answers fell short against the role. Turn those into a concrete, day-by-day practice plan you can actually finish this week.",
    "sp.pNoGaps": "Even strong performers grow faster with a plan. We'll turn your weakest interview dimensions into a 7-day daily routine.",
    "sp.build": "Build my study plan",
    "sp.building": "Building your plan…",
    "sp.yourPlan": "Your study plan",
    "sp.focusN": (n) => `Focus ${n}`,
    "sp.englishPrefix": "English",
    "sp.sevenDay": "7-day plan",
    "sp.dayN": (n) => `D${n}`,
    "sp.emailH": "Email me this plan",
    "sp.emailSub": "Sandbox mode — we'll deliver it once email is switched on.",
    "sp.emailSend": "Send it",
    "sp.emailSaved": "Saved",
    "sp.emailSending": "Saving…",
    // Pre-interview match preview (upload screen)
    "up.previewEyebrow": "Step 2 · Your match preview",
    "up.previewTitle": "Here's how your background lines up with this role.",
    "up.previewSub": "Based on your resume only — no interview answers yet. Use it to spot gaps to lean into during the practice.",
    "up.previewStart": "Start my interview",
    "up.previewBack": "← Change my documents",
    "up.previewComputing": "Scoring your match…",
    // Seniority (AI-detected, read-only)
    "sen.eyebrow": "Interview level (detected)",
    "sen.entry":      "Entry level",
    "sen.mid":        "Mid level",
    "sen.senior":     "Senior IC",
    "sen.managerial": "Managerial",
    "sen.executive":  "Executive",
    "sen.hintDocs": "We calibrated the interview to this level based on your documents. No manual override — that's the point of a realistic simulation.",
  },
  "pt-BR": {
    "brand.tagline": "Simulador de Entrevistas com IA",
    "common.copy": "Copiar",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Baixar Relatório de Avaliação",
    "rp.downloadsCardH2": "Obter meu Guia de Estudos",
    "rp.downloadsCardSub1": "Baixe seu relatório completo de desempenho, pergunta por pergunta.",
    "rp.downloadsCardSub2": "Receba um guia de estudos personalizado de 7 dias baseado no seu desempenho na entrevista.",
    "rp.downloadsCardBtn1": "Baixar Relatório",
    "rp.downloadsCardBtn2": "Baixar Guia de Estudos",
    "rp.downloadsCardBuilding": "Preparando…",
    "rp.pdfFormat": "Formato PDF",
    "iv.progress": (n, t, a) => `Pergunta ${n} de ${t}   ${a} respondida${a === 1 ? "" : "s"}`,
    "iv.finish": "Ver meu relatório",
    "iv.finish.busy": "Montando o relatório…",
    "iv.playQ": "Ouvir pergunta",
    "iv.stopQ": "Parar",
    "iv.loading": "Carregando…",
    "iv.femaleVoice": "Voz feminina",
    "iv.maleVoice": "Voz masculina",
    "iv.restartInterview": "Reiniciar entrevista",
    "iv.restartConfirm": "Reiniciar a entrevista? Seu progresso atual será perdido, mas o currículo, a descrição da vaga e as demais escolhas serão mantidos.",
    "iv.recordAria": "Iniciar gravação",
    "iv.stopAria": "Parar gravação",
    "iv.micAccessDenied": "Permita o acesso ao microfone no seu navegador.",
    "iv.interviewCompleteTitle": "Entrevista concluída!",
    "iv.interviewCompleteBody": "Parabéns! Você concluiu sua entrevista com sucesso. Agora você pode visualizar seu relatório completo de desempenho e seu plano de estudos personalizado.",
    "iv.progressCurrent": (n, total) => `Pergunta ${n} de ${total}`,
    "iv.progressAnswered": (n) => `${n} respondida${n === 1 ? "" : "s"}`,
    "iv.hiddenH": "Modo de compreensão auditiva",
    "iv.hiddenP": "O texto da pergunta está oculto. Ouça o entrevistador com atenção e responda com a sua voz. Você pode reproduzir a pergunta acima quando quiser.",
    "iv.reveal": "Mostrar a pergunta →",
    "iv.tapMic": "Toque no microfone para falar",
    "iv.recording": "Gravando a sua resposta",
    "iv.recorded": "Gravada — revise antes de enviar",
    "iv.rerecord": "Regravar",
    "iv.submit": "Enviar resposta",
    "iv.submitting": "Transcrevendo e avaliando…",
    "iv.transcriptLabel": "Transcrição",
    "iv.previous": "← Anterior",
    "iv.skipNext": "Pular para a próxima",
    "iv.lastHint": "Última pergunta — conclua acima quando estiver pronto.",
    "iv.yourQuestions": "Suas perguntas",
    "iv.hiddenSidebar": "Oculta — escute quando chegar aqui",
    "iv.answered": "Resposta gravada",
    "iv.answeredSub": "Você pode revisar o seu feedback no relatório final.",
    "iv.clarity": "Clareza",
    "iv.grammar": "Gramática",
    "iv.vocabulary": "Vocabulário",
    "iv.tone": "Tom",
    "iv.yourTranscript": "Sua transcrição",
    "iv.questionEyebrow": "Pergunta da entrevista",
    "iv.standardQuestion": "Pergunta clássica",
    "iv.roleQuestion": "Pergunta sobre a vaga",
    "iv.resumeQuestion": "Pergunta baseada no currículo",
    "iv.questionN": (n) => `Pergunta ${n}`,
    "iv.loadFail": "Não foi possível carregar sua entrevista.",
    "iv.answerSaved": "Resposta salva.",
    "iv.submitFail": "Falha ao enviar. Tente novamente.",
    "iv.reportFail": "Ainda não foi possível montar o relatório.",
    "iv.audioPlayFail": "Não foi possível reproduzir o áudio da pergunta.",
    "iv.audioUnavailable": "O áudio da pergunta está indisponível no momento. Leia a pergunta abaixo e continue.",
    "iv.stop": "Parar",
    "iv.warmupEye": "Pergunta de Aquecimento",
    "iv.warmupTitle": "Vamos começar com um aquecimento",
    "iv.warmupIntro": "Antes de iniciarmos a entrevista, faremos uma breve pergunta de aquecimento. Ela tem apenas o objetivo de ajudá-lo(a) a testar o microfone, sentir-se mais confortável falando em inglês e conhecer o funcionamento da entrevista. Sua resposta não será avaliada e não fará parte da sua pontuação ou dos relatórios da entrevista.",
    "iv.warmupBanner": "Este aquecimento é apenas para você se preparar. Nada será enviado para análise.",
    "iv.warmupStart": "Começar a gravar",
    "iv.warmupContinue": "Continuar para a entrevista",
    "iv.warmupQuestionEye": "Pergunta de aquecimento",
    "rp.download": "Baixar relatório",
    "rp.newInterview": "Nova entrevista",
    "rp.betaFeedback": "Feedback beta",
    "rp.betaFeedbackStatusError": "O feedback beta está temporariamente indisponível.",
    "rp.repracticeH": "Pratique Estas Perguntas Novamente",
    "rp.repracticeP": "Aplique seu feedback repetindo as mesmas perguntas da entrevista.",
    "rp.repracticeMeta": "1 nova prática disponível · Usa 1 crédito",
    "rp.repracticeCta": "Praticar estas perguntas novamente",
    "rp.repracticeUsed": "Você já concluiu sua tentativa de nova prática para esta entrevista.",
    "rp.repracticeConfirmTitle": "Praticar Estas Perguntas Novamente?",
    "rp.repracticeConfirmIntro": "Você pode praticar esta entrevista novamente uma vez usando exatamente as mesmas perguntas. Isto usará 1 crédito.",
    "rp.repracticeConfirmBeforeH": "Antes de começar:",
    "rp.repracticeConfirmBullet1": "Seu Relatório de Desempenho e Guia de Estudos continuarão disponíveis para download por 30 dias.",
    "rp.repracticeConfirmBullet2": "Não saia da sua conta. Sua Nova Prática deve ser concluída durante sua sessão de login atual.",
    "rp.repracticeConfirmYes": "Iniciar Nova Prática",
    "rp.repracticeConfirmNo": "Cancelar",
    "rp.repracticeStarting": "Iniciando a nova prática\u2026",
    "rp.repracticeErrGeneric": "Não foi possível iniciar a nova prática. Tente novamente.",
    "rp.repracticeErrAlreadyUsed": "Você já usou sua nova prática para esta entrevista.",
    "rp.repracticeErrNoCredits": "Você não tem créditos de entrevista disponíveis.",
    "rp.overall": "Desempenho geral",
    "rp.composite": "Média de clareza · gramática · vocabulário · tom profissional",
    "rp.answeredOf": (a, t) => `Você respondeu ${a} de ${t} perguntas. Role para baixo para ver o feedback por pergunta — clareza, gramática, vocabulário e tom profissional.`,
    "rp.clarity": "Clareza",
    "rp.grammar": "Gramática",
    "rp.vocabulary": "Vocabulário",
    "rp.protone": "Tom profissional",
    "rp.fluency": "Fluência",
    "rp.relevance": "Relevância à pergunta",
    "rp.supportingEvidence": "Evidências e exemplos",
    "rp.organization": "Organização e estrutura",
    "rp.professionalCompetency": "Competência profissional",
    "rp.evidenceOfResults": "Evidência de resultados",
    "rp.professionalPresence": "Presença profissional",
    "rp.interviewPerformance": "Desempenho na entrevista",
    "rp.englishLanguage": "Comunicação em Inglês Profissional",
    "rp.englishInsufficient": "Resposta muito curta para avaliar o inglês.",
    "rp.englishAssessedOf": (a, t) => `avaliado em ${a} de ${t} respostas`,
    "rp.summary": "Resumo",
    "rp.summaryH": "Seu Desempenho na Entrevista em Resumo",
    "rp.summaryP": "Abaixo está seu feedback, pergunta por pergunta. Veja o que você fez bem, onde pode melhorar e quais áreas terão o maior impacto na sua próxima entrevista.",
    "rp.bestArea": "Ponto mais forte",
    "rp.focusNext": "Foco a seguir",
    "rp.perQuestion": "Feedback por pergunta",
    "rp.answeredCount": (a, t) => `${a} de ${t} respondida${t === 1 ? "" : "s"}`,
    "rp.notAnswered": "Sem resposta",
    "rp.overallShort": "Geral",
    "rp.yourTranscript": "Sua transcrição",
    "rp.suggestedAnswer": "Resposta modelo sugerida (em inglês)",
    "rp.strengths": "Pontos fortes",
    "rp.improve": "A melhorar",
    "jm.title": "Compatibilidade com a vaga",
    "jm.subtitle": "Como o seu currículo + respostas se comparam com as habilidades exigidas na descrição da vaga.",
    "jm.matchScore": "Pontuação de compatibilidade",
    "jm.matched": "compatível",
    "jm.partial": "parcial",
    "jm.gap": "lacuna",
    "jm.gaps": "lacunas",
    "jm.covered": (m, t) => `Habilidades exigidas · ${m}/${t} contempladas`,
    "jm.statusMatch": "Compatível",
    "jm.statusPartial": "Parcial",
    "jm.statusGap": "Lacuna",
    "sp.eyebrow": "Próximo passo",
    "sp.hGaps": (n) => "suas " + (n === 1 ? "principais lacunas" : `${n} principais lacunas`),
    "sp.hFallback": "principais áreas a melhorar",
    "sp.h": (target) => `Receba um plano de estudos de 7 dias personalizado para ${target}.`,
    "sp.pWithGaps": "Identificamos onde o seu currículo + respostas ficaram aquém do que a vaga pede. Vamos transformar isso em um plano concreto, dia a dia, que você consegue concluir nesta semana.",
    "sp.pNoGaps": "Mesmo quem já se sai bem cresce mais rápido com um plano. Vamos transformar as suas dimensões mais fracas em uma rotina diária de 7 dias.",
    "sp.build": "Montar meu plano de estudos",
    "sp.building": "Montando o seu plano…",
    "sp.yourPlan": "Seu plano de estudos",
    "sp.focusN": (n) => `Foco ${n}`,
    "sp.englishPrefix": "Inglês",
    "sp.sevenDay": "Plano de 7 dias",
    "sp.dayN": (n) => `D${n}`,
    "sp.emailH": "Enviar este plano por e-mail",
    "sp.emailSub": "Modo sandbox — vamos enviar assim que o e-mail estiver ativo.",
    "sp.emailSend": "Enviar",
    "sp.emailSaved": "Salvo",
    "sp.emailSending": "Salvando…",
    "up.previewEyebrow": "Passo 2 · Prévia da sua compatibilidade",
    "up.previewTitle": "Veja como o seu histórico se alinha com esta vaga.",
    "up.previewSub": "Baseado apenas no seu currículo — ainda sem respostas de entrevista. Use isto para identificar lacunas que você pode reforçar durante a prática.",
    "up.previewStart": "Iniciar minha entrevista",
    "up.previewBack": "← Alterar meus documentos",
    "up.previewComputing": "Calculando a sua compatibilidade…",
    "sen.eyebrow": "Nível da entrevista (detectado)",
    "sen.entry":      "Nível inicial",
    "sen.mid":        "Nível pleno",
    "sen.senior":     "Sênior",
    "sen.managerial": "Gestão",
    "sen.executive":  "Executivo(a)",
    "sen.hintDocs": "Ajustamos a entrevista para este nível com base nos seus documentos. Não é possível alterar — é justamente isso que torna a simulação realista.",
  },

  // Latin American Spanish — used on the Report page (Component 2). Other
  // pages fall back to English keys until their component turn.
  "es-419": {
    "brand.tagline": "Simulador de Entrevistas con IA",
    "common.copy": "Copiar",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Descargar Informe de Evaluación",
    "rp.downloadsCardH2": "Obtener mi Guía de Estudio",
    "rp.downloadsCardSub1": "Descargue su informe completo de desempeño, pregunta por pregunta.",
    "rp.downloadsCardSub2": "Reciba una guía de estudio personalizada de 7 días basada en su desempeño en la entrevista.",
    "rp.downloadsCardBtn1": "Descargar Informe",
    "rp.downloadsCardBtn2": "Descargar Guía de Estudio",
    "rp.downloadsCardBuilding": "Preparando…",
    "rp.pdfFormat": "Formato PDF",
    // Interview page
    "iv.progress": (n, t, a) => `Pregunta ${n} de ${t}   ${a} respondida${a === 1 ? "" : "s"}`,
    "iv.finish": "Ver mi informe",
    "iv.finish.busy": "Generando el informe…",
    "iv.playQ": "Escuchar pregunta",
    "iv.stopQ": "Detener",
    "iv.stop": "Detener",
    "iv.warmupEye": "Pregunta de Calentamiento",
    "iv.warmupTitle": "Comencemos con un breve calentamiento",
    "iv.warmupIntro": "Antes de iniciar la entrevista, haremos una breve pregunta de calentamiento. Su único propósito es ayudarle a probar el micrófono, sentirse más cómodo/a hablando en inglés y familiarizarse con el funcionamiento de la entrevista. Su respuesta no será evaluada ni afectará su puntuación ni los informes.",
    "iv.warmupBanner": "Este calentamiento es solo para que se prepare. Nada se envía para análisis.",
    "iv.warmupStart": "Comenzar a grabar",
    "iv.warmupContinue": "Continuar a la entrevista",
    "iv.warmupQuestionEye": "Pregunta de calentamiento",
    "iv.loading": "Cargando…",
    "iv.femaleVoice": "Voz femenina",
    "iv.maleVoice": "Voz masculina",
    "iv.restartInterview": "Reiniciar entrevista",
    "iv.restartConfirm": "¿Reiniciar la entrevista? Su progreso actual se perderá, pero conservaremos su currículum, la descripción del puesto y sus otras selecciones.",
    "iv.recordAria": "Iniciar grabación",
    "iv.stopAria": "Detener grabación",
    "iv.micAccessDenied": "Permita el acceso al micrófono en su navegador.",
    "iv.interviewCompleteTitle": "¡Entrevista completada!",
    "iv.interviewCompleteBody": "¡Felicidades! Completó su entrevista con éxito. Ahora puede ver su informe completo de desempeño y su plan de estudio personalizado.",
    "iv.progressCurrent": (n, total) => `Pregunta ${n} de ${total}`,
    "iv.progressAnswered": (n) => `${n} respondida${n === 1 ? "" : "s"}`,
    "iv.hiddenH": "Modo de comprensión auditiva",
    "iv.hiddenP": "El texto de la pregunta está oculto. Escuche atentamente al entrevistador y responda con su voz. Puede reproducir la pregunta arriba cuando quiera.",
    "iv.reveal": "Mostrar la pregunta →",
    "iv.tapMic": "Toque el micrófono para hablar",
    "iv.recording": "Grabando su respuesta",
    "iv.recorded": "Grabada — revise antes de enviar",
    "iv.rerecord": "Volver a grabar",
    "iv.submit": "Enviar respuesta",
    "iv.submitting": "Transcribiendo y evaluando…",
    "iv.transcriptLabel": "Transcripción",
    "iv.previous": "← Anterior",
    "iv.skipNext": "Saltar a la siguiente",
    "iv.lastHint": "Última pregunta — finalice arriba cuando esté listo/a.",
    "iv.yourQuestions": "Sus preguntas",
    "iv.hiddenSidebar": "Oculta — escuche cuando llegue aquí",
    "iv.answered": "Respuesta grabada",
    "iv.answeredSub": "Puede revisar su feedback en el informe final.",
    "iv.clarity": "Claridad",
    "iv.grammar": "Gramática",
    "iv.vocabulary": "Vocabulario",
    "iv.tone": "Tono",
    "iv.yourTranscript": "Su transcripción",
    "iv.questionEyebrow": "Pregunta de la entrevista",
    "iv.standardQuestion": "Pregunta clásica",
    "iv.roleQuestion": "Pregunta sobre el puesto",
    "iv.resumeQuestion": "Pregunta basada en el currículum",
    "iv.questionN": (n) => `Pregunta ${n}`,
    "iv.loadFail": "No pudimos cargar su entrevista.",
    "iv.answerSaved": "Respuesta guardada.",
    "iv.submitFail": "Error al enviar. Inténtelo de nuevo.",
    "iv.reportFail": "Aún no pudimos generar el informe.",
    "iv.audioPlayFail": "No pudimos reproducir el audio de la pregunta.",
    "iv.audioUnavailable": "El audio de la pregunta no está disponible en este momento. Lea la pregunta a continuación y continúe.",
    "rp.download": "Descargar informe",
    "rp.newInterview": "Nueva entrevista",
    "rp.betaFeedback": "Feedback beta",
    "rp.betaFeedbackStatusError": "El feedback beta no está disponible por el momento.",
    "rp.repracticeH": "Practique Estas Preguntas Nuevamente",
    "rp.repracticeP": "Aplique sus comentarios repitiendo las mismas preguntas de la entrevista.",
    "rp.repracticeMeta": "1 nueva práctica disponible · Usa 1 crédito",
    "rp.repracticeCta": "Practicar estas preguntas nuevamente",
    "rp.repracticeUsed": "Ya completó su intento de nueva práctica para esta entrevista.",
    "rp.repracticeConfirmTitle": "\u00bfPracticar Estas Preguntas Nuevamente?",
    "rp.repracticeConfirmIntro": "Usted puede practicar esta entrevista una vez más utilizando exactamente las mismas preguntas. Esto utilizará 1 crédito.",
    "rp.repracticeConfirmBeforeH": "Antes de comenzar:",
    "rp.repracticeConfirmBullet1": "Su Informe de Desempeño y su Guía de Estudio seguirán disponibles para descargar durante 30 días.",
    "rp.repracticeConfirmBullet2": "No cierre sesión. Su Nueva Práctica debe completarse durante su sesión actual.",
    "rp.repracticeConfirmYes": "Iniciar Nueva Práctica",
    "rp.repracticeConfirmNo": "Cancelar",
    "rp.repracticeStarting": "Iniciando la nueva práctica\u2026",
    "rp.repracticeErrGeneric": "No se pudo iniciar la nueva práctica. Inténtelo de nuevo.",
    "rp.repracticeErrAlreadyUsed": "Ya utilizó su nueva práctica para esta entrevista.",
    "rp.repracticeErrNoCredits": "No tiene créditos de entrevista disponibles.",
    "rp.overall": "Desempeño general",
    "rp.composite": "Composición de claridad · gramática · vocabulario · tono profesional",
    "rp.answeredOf": (a, t) => `Completó ${a} de ${t} preguntas. Desplácese hacia abajo para ver el feedback pregunta por pregunta — claridad, gramática, vocabulario y tono profesional.`,
    "rp.clarity": "Claridad",
    "rp.grammar": "Gramática",
    "rp.vocabulary": "Vocabulario",
    "rp.protone": "Tono profesional",
    "rp.fluency": "Fluidez",
    "rp.relevance": "Relevancia a la pregunta",
    "rp.supportingEvidence": "Evidencia y ejemplos",
    "rp.organization": "Organización y estructura",
    "rp.professionalCompetency": "Competencia profesional",
    "rp.evidenceOfResults": "Evidencia de resultados",
    "rp.professionalPresence": "Presencia profesional",
    "rp.interviewPerformance": "Desempeño en la entrevista",
    "rp.englishLanguage": "Comunicación en Inglés Profesional",
    "rp.englishInsufficient": "Respuesta demasiado breve para evaluar su inglés.",
    "rp.englishAssessedOf": (a, t) => `evaluado en ${a} de ${t} respuestas`,
    "rp.summary": "Resumen",
    "rp.summaryH": "Su Desempeño en la Entrevista de un Vistazo",
    "rp.summaryP": "A continuación encontrará comentarios sobre cada pregunta. Revise lo que hizo bien, dónde puede mejorar y qué áreas tendrán el mayor impacto en su próxima entrevista.",
    "rp.bestArea": "Punto más fuerte",
    "rp.focusNext": "Próximo enfoque",
    "rp.perQuestion": "Feedback por pregunta",
    "rp.answeredCount": (a, t) => `${a} de ${t} respondida${t === 1 ? "" : "s"}`,
    "rp.notAnswered": "Sin respuesta",
    "rp.overallShort": "General",
    "rp.yourTranscript": "Su transcripción",
    "rp.suggestedAnswer": "Respuesta modelo sugerida (en inglés)",
    "rp.strengths": "Fortalezas",
    "rp.improve": "A mejorar",
    "jm.title": "Compatibilidad con el puesto",
    "jm.subtitle": "Cómo su currículum + respuestas se comparan con las habilidades requeridas en la descripción del puesto.",
    "jm.matchScore": "Puntuación de compatibilidad",
    "jm.matched": "compatible",
    "jm.partial": "parcial",
    "jm.gap": "brecha",
    "jm.gaps": "brechas",
    "jm.covered": (m, t) => `Habilidades requeridas · ${m}/${t} cubiertas`,
    "jm.statusMatch": "Compatible",
    "jm.statusPartial": "Parcial",
    "jm.statusGap": "Brecha",
    "sp.eyebrow": "Próximo paso",
    "sp.hGaps": (n) => (n === 1 ? "su principal brecha" : `sus ${n} principales brechas`),
    "sp.hFallback": "principales áreas a mejorar",
    "sp.h": (target) => `Obtenga un plan de estudio personalizado de 7 días para ${target}.`,
    "sp.pWithGaps": "Identificamos dónde su currículum + respuestas se quedaron cortos frente al puesto. Convirtámoslo en un plan concreto, día a día, que pueda terminar esta semana.",
    "sp.pNoGaps": "Incluso quienes tienen buen desempeño crecen más rápido con un plan. Convertiremos sus dimensiones más débiles en una rutina diaria de 7 días.",
    "sp.build": "Crear mi plan de estudio",
    "sp.building": "Creando su plan…",
    "sp.yourPlan": "Su plan de estudio",
    "sp.focusN": (n) => `Enfoque ${n}`,
    "sp.englishPrefix": "Inglés",
    "sp.sevenDay": "Plan de 7 días",
    "sp.dayN": (n) => `D${n}`,
    "sp.emailH": "Envíame este plan por correo",
    "sp.emailSub": "Modo sandbox — se lo enviaremos cuando el correo esté activo.",
    "sp.emailSend": "Enviar",
    "sp.emailSaved": "Guardado",
    "sp.emailSending": "Guardando…",
    "sen.eyebrow": "Nivel de la entrevista (detectado)",
    "sen.entry":      "Nivel inicial",
    "sen.mid":        "Nivel medio",
    "sen.senior":     "Sénior IC",
    "sen.managerial": "Gerencial",
    "sen.executive":  "Ejecutivo/a",
    "sen.hintDocs": "Calibramos la entrevista a este nivel según sus documentos. No hay opción manual — ese es el punto de una simulación realista.",
  },

  // Canadian French — used on the Report page (Component 2).
  "fr-CA": {
    "brand.tagline": "Simulateur d’entrevues avec IA",
    "common.copy": "Copier",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Télécharger le rapport d’évaluation",
    "rp.downloadsCardH2": "Obtenir mon guide d’étude",
    "rp.downloadsCardSub1": "Téléchargez votre rapport de performance complet, question par question.",
    "rp.downloadsCardSub2": "Recevez un guide d’étude personnalisé de 7 jours basé sur votre performance en entrevue.",
    "rp.downloadsCardBtn1": "Télécharger le rapport",
    "rp.downloadsCardBtn2": "Télécharger le guide d’étude",
    "rp.downloadsCardBuilding": "Préparation…",
    "rp.pdfFormat": "Format PDF",
    // Interview page
    "iv.progress": (n, t, a) => `Question ${n} sur ${t}   ${a} répondue${a === 1 ? "" : "s"}`,
    "iv.finish": "Voir mon rapport",
    "iv.finish.busy": "Préparation du rapport…",
    "iv.playQ": "Écouter la question",
    "iv.stopQ": "Arrêter",
    "iv.stop": "Arrêter",
    "iv.warmupEye": "Question d’échauffement",
    "iv.warmupTitle": "Commençons par un échauffement",
    "iv.warmupIntro": "Avant de commencer l’entrevue, nous allons faire une brève question d’échauffement. Son seul but est de vous aider à tester votre microphone, à vous sentir plus à l’aise pour parler en anglais et à comprendre comment se déroule l’entrevue. Votre réponse n’est pas évaluée et n’affecte ni votre note ni les rapports.",
    "iv.warmupBanner": "Cet échauffement sert uniquement à vous préparer. Rien n’est envoyé pour analyse.",
    "iv.warmupStart": "Commencer l’enregistrement",
    "iv.warmupContinue": "Continuer vers l’entrevue",
    "iv.warmupQuestionEye": "Question d’échauffement",
    "iv.loading": "Chargement…",
    "iv.femaleVoice": "Voix féminine",
    "iv.maleVoice": "Voix masculine",
    "iv.restartInterview": "Recommencer l’entrevue",
    "iv.restartConfirm": "Recommencer l’entrevue ? Votre progression actuelle sera perdue, mais nous conserverons votre CV, la description du poste et vos autres sélections.",
    "iv.recordAria": "Démarrer l’enregistrement",
    "iv.stopAria": "Arrêter l’enregistrement",
    "iv.micAccessDenied": "Autorisez l’accès au microphone dans votre navigateur.",
    "iv.interviewCompleteTitle": "Entrevue terminée !",
    "iv.interviewCompleteBody": "Félicitations ! Vous avez terminé votre entrevue avec succès. Vous pouvez maintenant consulter votre rapport de performance complet et votre plan d’étude personnalisé.",
    "iv.progressCurrent": (n, total) => `Question ${n} sur ${total}`,
    "iv.progressAnswered": (n) => `${n} répondue${n === 1 ? "" : "s"}`,
    "iv.hiddenH": "Mode compréhension auditive",
    "iv.hiddenP": "Le texte de la question est masqué. Écoutez attentivement l’intervieweur et répondez à voix haute. Vous pouvez rejouer la question ci-dessus à tout moment.",
    "iv.reveal": "Afficher la question →",
    "iv.tapMic": "Touchez le micro pour parler",
    "iv.recording": "Enregistrement de votre réponse",
    "iv.recorded": "Enregistrée — révisez avant l’envoi",
    "iv.rerecord": "Réenregistrer",
    "iv.submit": "Envoyer la réponse",
    "iv.submitting": "Transcription et évaluation…",
    "iv.transcriptLabel": "Transcription",
    "iv.previous": "← Précédente",
    "iv.skipNext": "Passer à la suivante",
    "iv.lastHint": "Dernière question — terminez ci-dessus quand vous êtes prêt(e).",
    "iv.yourQuestions": "Vos questions",
    "iv.hiddenSidebar": "Masquée — écoutez quand vous arrivez ici",
    "iv.answered": "Réponse enregistrée",
    "iv.answeredSub": "Vous pouvez revoir votre rétroaction dans le rapport final.",
    "iv.clarity": "Clarté",
    "iv.grammar": "Grammaire",
    "iv.vocabulary": "Vocabulaire",
    "iv.tone": "Ton",
    "iv.yourTranscript": "Votre transcription",
    "iv.questionEyebrow": "Question d’entrevue",
    "iv.standardQuestion": "Question classique",
    "iv.roleQuestion": "Question spécifique au poste",
    "iv.resumeQuestion": "Question basée sur le CV",
    "iv.questionN": (n) => `Question ${n}`,
    "iv.loadFail": "Impossible de charger votre entrevue.",
    "iv.answerSaved": "Réponse enregistrée.",
    "iv.submitFail": "Échec de l’envoi. Réessayez.",
    "iv.reportFail": "Impossible de générer le rapport pour l’instant.",
    "iv.audioPlayFail": "Impossible de lire l’audio de la question.",
    "iv.audioUnavailable": "L’audio de la question n’est pas disponible pour le moment. Veuillez lire la question ci-dessous et continuer.",
    "rp.download": "Télécharger le rapport",
    "rp.newInterview": "Nouvelle entrevue",
    "rp.betaFeedback": "Feedback bêta",
    "rp.betaFeedbackStatusError": "Le feedback bêta est temporairement indisponible.",
    "rp.repracticeH": "Pratiquez de nouveau ces questions",
    "rp.repracticeP": "Appliquez votre rétroaction en refaisant les mêmes questions d\u2019entrevue.",
    "rp.repracticeMeta": "1 nouvelle pratique disponible · Utilisez 1 crédit",
    "rp.repracticeCta": "Pratiquer de nouveau ces questions",
    "rp.repracticeUsed": "Vous avez terminé votre tentative de nouvelle pratique pour cette entrevue.",
    "rp.repracticeConfirmTitle": "Pratiquer de nouveau ces questions?",
    "rp.repracticeConfirmIntro": "Vous pouvez refaire cette entrevue une seule fois avec exactement les mêmes questions. Cela utilisera 1 crédit.",
    "rp.repracticeConfirmBeforeH": "Avant de commencer :",
    "rp.repracticeConfirmBullet1": "Votre rapport de rendement et votre guide d’étude resteront disponibles au téléchargement pendant 30 jours.",
    "rp.repracticeConfirmBullet2": "Ne vous déconnectez pas. Votre Nouvelle pratique doit être terminée pendant votre session de connexion actuelle.",
    "rp.repracticeConfirmYes": "Démarrer la Nouvelle pratique",
    "rp.repracticeConfirmNo": "Annuler",
    "rp.repracticeStarting": "Démarrage de la nouvelle pratique\u2026",
    "rp.repracticeErrGeneric": "Impossible de démarrer la nouvelle pratique. Veuillez réessayer.",
    "rp.repracticeErrAlreadyUsed": "Vous avez déjà utilisé votre nouvelle pratique pour cette entrevue.",
    "rp.repracticeErrNoCredits": "Vous n\u2019avez plus de crédits d\u2019entrevue disponibles.",
    "rp.overall": "Performance globale",
    "rp.composite": "Composite de clarté · grammaire · vocabulaire · ton professionnel",
    "rp.answeredOf": (a, t) => `Vous avez complété ${a} des ${t} questions. Faites défiler vers le bas pour voir la rétroaction question par question — clarté, grammaire, vocabulaire et ton professionnel.`,
    "rp.clarity": "Clarté",
    "rp.grammar": "Grammaire",
    "rp.vocabulary": "Vocabulaire",
    "rp.protone": "Ton professionnel",
    "rp.fluency": "Fluidité",
    "rp.relevance": "Pertinence à la question",
    "rp.supportingEvidence": "Preuves et exemples",
    "rp.organization": "Organisation et structure",
    "rp.professionalCompetency": "Compétence professionnelle",
    "rp.evidenceOfResults": "Preuve de résultats",
    "rp.professionalPresence": "Présence professionnelle",
    "rp.interviewPerformance": "Performance en entrevue",
    "rp.englishLanguage": "Communication en anglais professionnel",
    "rp.englishInsufficient": "Réponse trop courte pour évaluer votre anglais.",
    "rp.englishAssessedOf": (a, t) => `évalué sur ${a} des ${t} réponses`,
    "rp.summary": "Résumé",
    "rp.summaryH": "Votre Performance en Entrevue en un Coup d\u2019Œil",
    "rp.summaryP": "Vous trouverez ci-dessous votre rétroaction, question par question. Voyez ce que vous avez bien réussi, les aspects que vous pouvez améliorer et les éléments qui auront le plus grand impact sur votre prochaine entrevue.",
    "rp.bestArea": "Point le plus fort",
    "rp.focusNext": "Prochain focus",
    "rp.perQuestion": "Rétroaction par question",
    "rp.answeredCount": (a, t) => `${a} sur ${t} répondue${t === 1 ? "" : "s"}`,
    "rp.notAnswered": "Sans réponse",
    "rp.overallShort": "Global",
    "rp.yourTranscript": "Votre transcription",
    "rp.suggestedAnswer": "Réponse modèle suggérée (en anglais)",
    "rp.strengths": "Points forts",
    "rp.improve": "À améliorer",
    "jm.title": "Compatibilité avec le poste",
    "jm.subtitle": "Comment votre CV + vos réponses se comparent aux compétences requises dans la description du poste.",
    "jm.matchScore": "Score de compatibilité",
    "jm.matched": "compatible",
    "jm.partial": "partiel",
    "jm.gap": "écart",
    "jm.gaps": "écarts",
    "jm.covered": (m, t) => `Compétences requises · ${m}/${t} couvertes`,
    "jm.statusMatch": "Compatible",
    "jm.statusPartial": "Partiel",
    "jm.statusGap": "Écart",
    "sp.eyebrow": "Prochaine étape",
    "sp.hGaps": (n) => (n === 1 ? "votre principale lacune" : `vos ${n} principales lacunes`),
    "sp.hFallback": "principales zones d’amélioration",
    "sp.h": (target) => `Obtenez un plan d’étude personnalisé de 7 jours pour ${target}.`,
    "sp.pWithGaps": "Nous avons repéré où votre CV + vos réponses n’ont pas tenu la route face au poste. Transformons cela en un plan concret, jour après jour, que vous pouvez vraiment terminer cette semaine.",
    "sp.pNoGaps": "Même les personnes performantes progressent plus vite avec un plan. Nous transformerons vos dimensions les plus faibles en une routine quotidienne de 7 jours.",
    "sp.build": "Créer mon plan d’étude",
    "sp.building": "Création de votre plan…",
    "sp.yourPlan": "Votre plan d’étude",
    "sp.focusN": (n) => `Focus ${n}`,
    "sp.englishPrefix": "Anglais",
    "sp.sevenDay": "Plan de 7 jours",
    "sp.dayN": (n) => `J${n}`,
    "sp.emailH": "M’envoyer ce plan par courriel",
    "sp.emailSub": "Mode bac à sable — nous vous l’enverrons une fois le courriel activé.",
    "sp.emailSend": "Envoyer",
    "sp.emailSaved": "Enregistré",
    "sp.emailSending": "Enregistrement…",
    "sen.eyebrow": "Niveau de l’entrevue (détecté)",
    "sen.entry":      "Niveau débutant",
    "sen.mid":        "Niveau intermédiaire",
    "sen.senior":     "Sénior IC",
    "sen.managerial": "Direction",
    "sen.executive":  "Cadre supérieur",
    "sen.hintDocs": "Nous avons calibré l’entrevue à ce niveau en fonction de vos documents. Aucun ajustement manuel — c’est ce qui rend la simulation réaliste.",
  },
};

// Legacy aliases: the OLD `session.language` field used the codes
// "pt-br" (Portuguese) and "both" (bilingual PT-BR + EN); both are mapped
// to the pt-BR bundle so historical sessions still render.
STRINGS["pt-br"] = STRINGS["pt-BR"];
STRINGS["both"]  = STRINGS["pt-BR"];


/**
 * Normalise whatever the caller passes into a bundle key.
 * - `null | undefined | ""` → `"en"`
 * - Exact BCP-47 code (`"pt-BR"`, `"es-419"`, `"fr-CA"`, `"en"`) → itself
 * - Legacy `"pt-br"` / `"both"` → `"pt-BR"`
 * - Anything Portuguese-flavored (`"pt"`, `"pt-PT"`, `"pt-br"`) → `"pt-BR"`
 * - Anything Spanish-flavored (`"es"`, `"es-MX"`, `"es-419"`) → `"es-419"`
 * - Anything French-flavored (`"fr"`, `"fr-CA"`, `"fr-FR"`) → `"fr-CA"`
 * - Anything else → `"en"`
 */
export function normalizeLang(input) {
  const raw = (input || "").toString().trim();
  if (!raw) return "en";
  if (STRINGS[raw]) return raw === "pt-br" || raw === "both" ? "pt-BR" : raw;
  const low = raw.toLowerCase();
  if (low.startsWith("pt") || low === "both") return "pt-BR";
  if (low.startsWith("es")) return "es-419";
  if (low.startsWith("fr")) return "fr-CA";
  return "en";
}


export function makeT(lang) {
  const code = normalizeLang(lang);
  const table = STRINGS[code] || STRINGS.en;
  return (key, ...args) => {
    const v = table[key];
    if (typeof v === "function") return v(...args);
    if (v !== undefined) return v;
    // Fallback to English if the localized bundle doesn't have this key yet.
    const fallback = STRINGS.en[key];
    if (typeof fallback === "function") return fallback(...args);
    return fallback ?? key;
  };
}
